import {
  useBackgroundFilters,
  useCallStateHooks,
  useParticipantViewContext,
  VideoPlaceholderProps,
} from "@stream-io/video-react-sdk";
import { useObservableEagerState, useObservableState } from "observable-hooks";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { match } from "ts-pattern";
import scheduleNextIcon from "../../../public/schedule-next-icon.svg";
import bookmarkAddIcon from "../../assets/icons/bookmark-add.png";
import { VidalifyLogoWithText } from "../../assets/vidalify-logo-with-text.fc";
import { Rx } from "../../prelude";

export module LiveSessionComponents {
  export const MutedIcon: React.FC = () => {
    return (
      <div className="bg-gray-800 text-white px-4 py-2 rounded-full flex items-center">
        <svg
          className="w-4 h-4 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
            clipRule="evenodd"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"
          />
        </svg>
        <span>Muted</span>
      </div>
    );
  };

  export module Notifications {
    export const DisplayTimeLeft: React.FC<{ message: string }> = ({
      message,
    }) => {
      return <div className="bg-blue-200 px-2 py-2 rounded-md">{message}</div>;
    };
  }

  interface CountdownTimerProps {
    initialMinutes: number;
    initialSeconds: number;
  }

  export const CountdownTimer: React.FC<CountdownTimerProps> = ({
    initialMinutes,
    initialSeconds,
  }) => {
    const [minutes, setMinutes] = useState(initialMinutes);
    const [seconds, setSeconds] = useState(initialSeconds);

    useEffect(() => {
      let interval = setInterval(() => {
        if (seconds > 0) {
          setSeconds(seconds - 1);
        }
        if (seconds === 0) {
          if (minutes === 0) {
            clearInterval(interval);
          } else {
            setMinutes(minutes - 1);
            setSeconds(59);
          }
        }
      }, 1000);

      return () => clearInterval(interval);
    }, [minutes, seconds]);

    return (
      <div>
        {minutes}:{seconds < 10 ? `0${seconds}` : seconds}
      </div>
    );
  };

  export namespace BottomLeftButtons {
    type ViewState = "collapsed" | "expanded" | "success";

    const ContainerView: React.FC<{
      icon: React.ReactNode;
      viewState$: Rx.BehaviorSubject<ViewState>;
      expandedContent: React.ReactNode;
      expandedButton: {
        title: string;
        onClick: () => void;
        width?: string;
      };
      successMessage: string;
      flashSignal$?: Rx.Subject<boolean>;
    }> = ({
      icon,
      viewState$,
      flashSignal$,
      expandedContent,
      expandedButton,
      successMessage,
    }) => {
      const viewState = useObservableEagerState(viewState$);
      const isFlashing$ = useMemo(
        () => flashSignal$ ?? new Rx.Subject<boolean>(),
        [flashSignal$]
      );
      const isFlashing = useObservableState(isFlashing$, false);

      useEffect(() => {
        if (isFlashing) {
          const timeout = setTimeout(() => {
            isFlashing$.next(false);
          }, 10000);
          return () => clearTimeout(timeout);
        }
      }, [isFlashing]);

      useEffect(() => {
        if (viewState === "success") {
          setTimeout(() => {
            viewState$.next("collapsed");
          }, 1800);
        }
      }, [viewState]);

      const roundIcon = () => (
        <div
          className={`w-[55px] aspect-square rounded-full flex justify-center items-center cursor-pointer
            ${isFlashing ? "animate-pulse bg-purple-500" : ""}
            `}
          onClick={() => {
            console.log("VIEW STATE! ", viewState);
            if (viewState === "expanded") {
              console.log("SETTING VIEW STATE TO COLLAPSED! ");
              viewState$.next("collapsed");
            } else {
              viewState$.next("expanded");
            }
          }}
        >
          <div className="w-[18px] aspect-square">
            {viewState === "success" ? (
              <img src="/bookmark-success-check.svg" />
            ) : (
              icon
            )}
          </div>
        </div>
      );

      return (
        <div
          className={`
       rounded-full text-white flex 
      border border-vid-black-800 bg-transparent-black
      h-[55px] ${viewState !== "collapsed" ? "pr-6" : "aspect-square"}
      `}
        >
          {roundIcon()}
          {match(viewState)
            .with("collapsed", () => <></>)
            .with("expanded", () => (
              <div className="flex-1 flex gap-4 py-[7.5px]">
                <div className="flex items-center">{expandedContent}</div>
                <div className="self-stretch ">
                  <ActionButton {...expandedButton} />
                </div>
              </div>
            ))
            .with("success", () => <div>{successMessage}</div>)
            .exhaustive()}
        </div>
      );
    };

    export const ToggleNavButton: React.FC<{
      onClick: () => void;
      arrowDirection: "left" | "right";
    }> = ({ onClick, arrowDirection }) => {
      return (
        <div
          className={`
     rounded-full text-white flex 
    border border-vid-black-800 bg-transparent-black
    h-[55px] 
    `}
        >
          <div
            className={`w-[55px] aspect-square rounded-full flex justify-center items-center cursor-pointer`}
            onClick={() => {
              onClick();
            }}
          >
            <div className="w-[18px] aspect-square">
              {arrowDirection === "left" ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="21"
                  viewBox="0 0 20 21"
                  fill="none"
                >
                  <path
                    d="M11 5L6 10.5L11 16"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="21"
                  viewBox="0 0 20 21"
                  fill="none"
                >
                  <path
                    d="M9 16L14 10.5L9 5"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
          </div>
        </div>
      );
    };

    const ActionButton: React.FC<{
      title: string;
      onClick: () => void;
      width?: string;
    }> = ({ title, onClick, width }) => {
      return (
        <button
          className={`
          h-full min-h-0 min-w-0
          bg-white text-vid-black-900 px-[26px] rounded-full flex justify-center items-center font-medium
          ${width ? `w-[${width}]` : ""}
          `}
          onClick={onClick}
        >
          {title}
        </button>
      );
    };

    export const NextScheduleReminder: React.FC<{
      onScheduleClick: () => void;
      onDismissClick: () => void;
      isFlashingPurple$: Rx.Subject<boolean>;
    }> = ({ onScheduleClick, onDismissClick, isFlashingPurple$ }) => {
      const viewState$ = useMemo(
        () => new Rx.BehaviorSubject<ViewState>("collapsed"),
        []
      );

      return (
        <ContainerView
          icon={<img src={scheduleNextIcon} alt="S" />}
          flashSignal$={isFlashingPurple$}
          successMessage="Session scheduled"
          expandedButton={{
            title: "Schedule",
            onClick: () => {
              onScheduleClick();
              viewState$.next("collapsed");
            },
          }}
          expandedContent={
            <div className={`flex items-center gap-4`}>
              <h4 className="font-medium text-white">Schedule next session</h4>
              <button
                className="bg-transparent text-white px-[26px] py-[13px] rounded-lg flex justify-center items-center font-medium"
                onClick={() => {
                  onDismissClick();
                  viewState$.next("collapsed");
                }}
              >
                Dismiss
              </button>
            </div>
          }
          viewState$={viewState$}
        />
      );
    };

    export const BookmarkButton: React.FC<{
      onBookmarkClick: () => void;
    }> = ({ onBookmarkClick }) => {
      const viewState$ = useMemo(
        () => new Rx.BehaviorSubject<ViewState>("collapsed"),
        []
      );
      return (
        <ContainerView
          icon={<img src={bookmarkAddIcon} alt="Bookmark" />}
          viewState$={viewState$}
          expandedButton={{
            title: "Create Bookmark",
            onClick: () => {
              onBookmarkClick();
              viewState$.next("success");
            },
          }}
          expandedContent={<div>Bookmark</div>}
          successMessage="Bookmark created"
        />
      );
    };

    export const NotesButton: React.FC<{
      onNotesClick: () => void;
    }> = ({ onNotesClick }) => {
      const viewState$ = useMemo(
        () => new Rx.BehaviorSubject<ViewState>("collapsed"),
        []
      );
      return (
        <ContainerView
          icon={<img src={"/stickynote-white.svg"} alt="N" />}
          expandedButton={{
            title: "Add note",
            onClick: () => {
              onNotesClick();
              viewState$.next("collapsed");
            },
          }}
          expandedContent={<div>Notes</div>}
          successMessage="Note added"
          viewState$={viewState$}
        />
      );
    };

    export const RecordSectionButton: React.FC<{
      onStartClick: () => void;
      onStopClick: () => void;
      isRecording$: Rx.Observable<boolean>;
    }> = ({ onStartClick, onStopClick, isRecording$ }) => {
      const isRecording = useObservableState(isRecording$, false);
      const viewState$ = useMemo(
        () => new Rx.BehaviorSubject<ViewState>("collapsed"),
        []
      );

      return (
        <ContainerView
          icon={
            <img
              src={
                isRecording
                  ? "/bookendRecording.gif"
                  : "/live-record-section-static-icon.png"
              }
              className="cursor-pointer w-full h-full"
            />
          }
          viewState$={viewState$}
          expandedButton={{
            title: isRecording ? "Stop" : "Start",
            onClick: () => {
              if (isRecording) {
                onStopClick();
                viewState$.next("success");
              } else {
                onStartClick();
              }
            },
          }}
          expandedContent={<div>Recording</div>}
          successMessage="Recording captured"
        />
      );
    };
  }
}

export const WaitingRoomContainer: React.FC<{
  onBackClick: () => void;
  leftSide: React.ReactNode;
  rightSide: React.ReactNode;
  onStartLink: string;
}> = ({ onBackClick, leftSide, rightSide, onStartLink }) => {
  return (
    <div className="flex-1 flex flex-col min-h-0 border-4">
      <nav
        className="basis-[80px] grow-0 shrink-0 flex items-center justify-between px-8"
        style={{
          boxShadow: "0 0 10px 0 rgba(0,0,0,0.1)",
        }}
      >
        <VidalifyLogoWithText />
        <button
          className="text-vid-purple font-bold"
          onClick={() => {
            onBackClick();
          }}
        >
          Back to dashboard
        </button>
      </nav>
      <div className="grow shrink flex gap-2 px-4 justify-center  min-h-0 py-20">
        <div className="flex-1">{leftSide}</div>
        <div className="flex-1 hidden md:flex md:flex-col">{rightSide}</div>
        {/* <div className="basis-[300px] grow-0 shrink w-[100px] bg-blue-400 min-h-0" /> */}
      </div>
      <WaitingRoomFooter onBackClick={onBackClick} onStartLink={onStartLink} />
    </div>
  );
};

const WaitingRoomFooter: React.FC<{
  onBackClick: () => void;
  onStartLink: string;
}> = ({ onBackClick, onStartLink }) => {
  return (
    <footer className="basis-[100px] grow-0 shrink-0 self-stretch border-t border-gray-200 bg-white flex justify-between items-center gap-8 md:gap-24 relative">
      <img className="absolute inset-0" src={"/dot-pattern.png"} />
      <div className="absolute inset-0 flex justify-between items-center gap-24 px-8 md:px-48 py-9 ">
        <button
          className={`
      py-4 px-8 flex justify-center items-center rounded-lg border border-vid-black-900 text-vid-black-900
      hover:bg-vid-black-900 hover:text-white bg-white
      `}
          onClick={() => {
            onBackClick();
          }}
        >
          Back
        </button>
        <Link
          className={`
      py-4 px-8 flex justify-center items-center rounded-lg 
      bg-vid-purple text-white
      `}
          to={onStartLink}
        >
          Start Session
        </Link>
      </div>
    </footer>
  );
};

export const IsRecordingIcon: React.FC = () => {
  return <div className="bg-red-700 h-2 w-2 rounded-full" />;
};

export const CustomParticipantViewUI = () => {
  const { participant } = useParticipantViewContext();

  return (
    <div className="absolute top-4 left-4 right-4 flex">
      <div className="flex-1 min-w-0 flex">
        {/* <div className="bg-gray-800 h-[34px] rounded-[12px] text-white px-3 py-[6px] font-semibold flex justify-center items-center gap-4">
          {participant.name}
        </div> */}
        <div className=" text-white  font-semibold flex justify-center items-center gap-4">
          {participant.name}
        </div>
      </div>
      <div className="flex-1 flex justify-center">
        {!participant.audioStream && <LiveSessionComponents.MutedIcon />}
      </div>
      <div className="flex-1 flex"></div>
    </div>
  );
};

export const CustomVideoPlaceholder = ({ style }: VideoPlaceholderProps) => {
  const { participant } = useParticipantViewContext();

  return (
    <div
      className="absolute inset-0 flex-1 flex flex-col justify-center items-center gap-4 bg-vid-black-900 text-white border rounded-[20px]"
      style={style}
    >
      {participant.image && (
        <div className="w-[40px] h-[40px] max-w-[40px] max-h-[40px] rounded-full">
          <img
            className="w-full h-full aspect-square rounded-full object-cover border border-vid-purple"
            src={participant.image}
            alt={participant.name}
          />
        </div>
      )}
      <h4 className="font-bold text-xl">{participant.name}</h4>
    </div>
  );
};

const BlurBackgroundNav: React.FC = () => {
  const {
    disableBackgroundFilter, // disables the filter
    applyBackgroundBlurFilter, // applies the blur filter
    applyBackgroundImageFilter, // applies the image filter
    backgroundImages, // list of available images
  } = useBackgroundFilters();
  return (
    <div className="flex justify-center gap-4 border p-8">
      <button
        className="rounded-full p-4 bg-white border"
        onClick={disableBackgroundFilter}
      >
        Disable blur
      </button>
      <button
        className="rounded-full p-4 bg-white border"
        onClick={() => applyBackgroundBlurFilter("high")}
      >
        Enable blur
      </button>
      <ul>
        {backgroundImages?.map((image) => (
          <li key={image}>
            <img src={image} alt="background" />
            <button onClick={() => applyBackgroundImageFilter(image)}>
              Apply background
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ScreenshareNav: React.FC = () => {
  const { useScreenShareState } = useCallStateHooks();
  const { screenShare, status } = useScreenShareState();
  return (
    <div className="flex flex-col">
      <button
        className="rounded-full p-4 bg-white border"
        onClick={() => {
          if (status === "enabled") {
            screenShare.disable();
          } else {
            screenShare.enable();
          }
        }}
      >
        {status === "enabled" ? "Stop screenshare" : "Start screenshare"}
      </button>
    </div>
  );
};

export const SettingsRightNav: React.FC<{ extra?: React.ReactNode }> = ({
  extra,
}) => {
  return (
    <div className="flex flex-col">
      <BlurBackgroundNav />
      <ScreenshareNav />
      {extra ?? <></>}
    </div>
  );
};
